import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { api } from '@/lib/api'
import type { Deposito } from '@/types/database'
import { PlusIcon, EditIcon, EyeIcon, EyeOffIcon, WarehouseIcon, UsersIcon, BoxIcon } from '@/components/shared/Icons'

// ── Tipos locales ────────────────────────────────────────────────────────────

interface UsuarioConDeposito {
  id: string
  nombre: string
  email: string
  nivel: number
  role_nombre: string
  activo: boolean
  deposito_id: string | null
  deposito_nombre: string | null
}

interface StockUnificadoItem {
  id: string
  nombre: string
  marca: string | null
  sku: string | null
  categoria: string | null
  stock_minimo: number
  stock_total: number
  por_deposito: Array<{ deposito_id: string; deposito: string; cantidad: number }> | null
}

// ── Hooks ────────────────────────────────────────────────────────────────────

function useDepositos() {
  return useQuery({
    queryKey: ['depositos-admin'],
    queryFn: () => api.get<Deposito[]>('/api/depositos?all=true'),
  })
}

function useUsuarios() {
  return useQuery({
    queryKey: ['usuarios-admin'],
    queryFn: () => api.get<UsuarioConDeposito[]>('/api/auth/usuarios'),
  })
}

function useStockUnificado() {
  return useQuery({
    queryKey: ['stock-unificado'],
    queryFn: () => api.get<StockUnificadoItem[]>('/api/stock/unificado'),
  })
}

function useGuardarDeposito() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, nombre }: { id?: string; nombre: string }) => {
      if (id) {
        await api.put(`/api/depositos/${id}`, { nombre: nombre.trim() })
      } else {
        await api.post('/api/depositos', { nombre: nombre.trim() })
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['depositos-admin'] })
      qc.invalidateQueries({ queryKey: ['depositos'] })
    },
  })
}

function useToggleDeposito() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, activo }: { id: string; activo: boolean }) => {
      await api.put(`/api/depositos/${id}`, { activo })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['depositos-admin'] })
      qc.invalidateQueries({ queryKey: ['depositos'] })
    },
  })
}

function useAsignarDeposito() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ usuarioId, depositoId }: { usuarioId: string; depositoId: string | null }) => {
      await api.put(`/api/auth/usuarios/${usuarioId}`, { deposito_id: depositoId })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['usuarios-admin'] })
    },
  })
}

// ── Tabs ─────────────────────────────────────────────────────────────────────

type Tab = 'depositos' | 'asignacion' | 'stock-unificado'

const TABS: { key: Tab; label: string; icon: React.ReactNode }[] = [
  { key: 'depositos', label: 'Depósitos', icon: <WarehouseIcon size={14} /> },
  { key: 'asignacion', label: 'Asignación de usuarios', icon: <UsersIcon size={14} /> },
  { key: 'stock-unificado', label: 'Stock unificado', icon: <BoxIcon size={14} /> },
]

// ── Página principal ──────────────────────────────────────────────────────────

export default function DepositosPage() {
  const [tab, setTab] = useState<Tab>('depositos')

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 'var(--me-space-6)' }}>
        <div>
          <div className="me-eyebrow" style={{ marginBottom: 'var(--me-space-2)' }}>Administracion</div>
          <h1 className="me-page-title">Depositos y Stock</h1>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--me-line)', marginBottom: 'var(--me-space-6)' }}>
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 16px', border: 'none', background: 'none', cursor: 'pointer',
              fontFamily: 'var(--me-sans)', fontSize: 13, fontWeight: tab === t.key ? 600 : 400,
              color: tab === t.key ? 'var(--me-text)' : 'var(--me-text-3)',
              borderBottom: tab === t.key ? '2px solid var(--me-acid)' : '2px solid transparent',
              marginBottom: -1, borderRadius: 0,
              transition: 'color var(--me-dur-short)',
            }}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {tab === 'depositos' && <TabDepositos />}
      {tab === 'asignacion' && <TabAsignacion />}
      {tab === 'stock-unificado' && <TabStockUnificado />}
    </div>
  )
}

// ── Tab 1: CRUD depósitos ─────────────────────────────────────────────────────

function TabDepositos() {
  const { data: depositos = [], isLoading } = useDepositos()
  const guardar = useGuardarDeposito()
  const toggle = useToggleDeposito()

  const [modalOpen, setModalOpen] = useState(false)
  const [editando, setEditando] = useState<Deposito | null>(null)
  const [nombre, setNombre] = useState('')

  function abrirNuevo() { setEditando(null); setNombre(''); setModalOpen(true) }
  function abrirEditar(d: Deposito) { setEditando(d); setNombre(d.nombre); setModalOpen(true) }
  function cerrar() { setModalOpen(false); setEditando(null); setNombre('') }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!nombre.trim()) { toast.error('El nombre es obligatorio'); return }
    try {
      await guardar.mutateAsync({ id: editando?.id, nombre })
      toast.success(editando ? 'Deposito actualizado' : 'Deposito creado')
      cerrar()
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Error al guardar')
    }
  }

  async function handleToggle(d: Deposito) {
    try {
      await toggle.mutateAsync({ id: d.id, activo: !d.activo })
      toast.success(d.activo ? 'Deposito desactivado' : 'Deposito activado')
    } catch {
      toast.error('Error al cambiar estado')
    }
  }

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 'var(--me-space-4)' }}>
        <button className="me-btn me-btn-primary" onClick={abrirNuevo}>
          <PlusIcon size={15} /> Nuevo deposito
        </button>
      </div>

      <div style={{ fontSize: 13, color: 'var(--me-text-3)', marginBottom: 'var(--me-space-5)', maxWidth: 520 }}>
        Los depositos representan ubicaciones fisicas donde se almacena stock. Cada producto tiene stock independiente por deposito.
      </div>

      {isLoading ? (
        <Skeleton />
      ) : depositos.length === 0 ? (
        <EmptyState onNuevo={abrirNuevo} />
      ) : (
        <div className="me-card" style={{ overflow: 'hidden', padding: 0 }}>
          <table className="me-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Nombre</th>
                <th style={{ textAlign: 'center' }}>Estado</th>
                <th style={{ textAlign: 'right', color: 'var(--me-text-4)', fontWeight: 500 }}>Creado</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {depositos.map((d, i) => (
                <tr key={d.id} style={{ opacity: d.activo ? 1 : 0.45, background: i % 2 !== 0 ? 'var(--me-surface-2)' : 'transparent' }}>
                  <td style={{ fontWeight: 500 }}>{d.nombre}</td>
                  <td style={{ textAlign: 'center' }}>
                    <span className={`me-pill ${d.activo ? 'me-pill-ok' : 'me-pill-neutral'}`}>
                      {d.activo ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td style={{ textAlign: 'right', fontSize: 12, fontFamily: 'var(--me-mono)', color: 'var(--me-text-4)' }}>
                    {new Date(d.created_at).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                  </td>
                  <td>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 4 }}>
                      <ActionBtn onClick={() => abrirEditar(d)} title="Renombrar"><EditIcon size={14} /></ActionBtn>
                      <ActionBtn onClick={() => handleToggle(d)} title={d.activo ? 'Desactivar' : 'Activar'}>
                        {d.activo ? <EyeOffIcon size={14} /> : <EyeIcon size={14} />}
                      </ActionBtn>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modalOpen && (
        <>
          <div className="me-overlay-in" onClick={cerrar}
            style={{ position: 'fixed', inset: 0, zIndex: 40, background: 'rgba(0,0,0,0.28)' }} />
          <div className="me-modal-in" style={{
            position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
            zIndex: 41, width: 360, background: 'var(--me-surface)',
            borderRadius: 16, boxShadow: 'var(--me-shadow-modal)', padding: 'var(--me-space-6)',
          }}>
            <h2 className="me-section-title" style={{ marginBottom: 'var(--me-space-4)' }}>
              {editando ? 'Renombrar deposito' : 'Nuevo deposito'}
            </h2>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--me-space-4)' }}>
              <div>
                <label className="me-label">Nombre *</label>
                <input className="me-input" style={{ width: '100%' }} type="text" value={nombre}
                  onChange={e => setNombre(e.target.value)} placeholder="Ej: Principal, Deposito 2, Freezer..."
                  autoComplete="off" autoFocus />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className="me-btn me-btn-ghost" onClick={cerrar} style={{ flex: 1 }}>Cancelar</button>
                <button type="submit" className="me-btn me-btn-primary" style={{ flex: 2 }} disabled={guardar.isPending}>
                  {guardar.isPending ? 'Guardando...' : editando ? 'Guardar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </>
      )}
    </>
  )
}

// ── Tab 2: Asignación de usuarios ─────────────────────────────────────────────

function TabAsignacion() {
  const { data: usuarios = [], isLoading: loadingUsuarios } = useUsuarios()
  const { data: depositos = [], isLoading: loadingDepositos } = useDepositos()
  const asignar = useAsignarDeposito()

  const isLoading = loadingUsuarios || loadingDepositos

  async function handleAsignar(usuarioId: string, depositoId: string | null) {
    try {
      await asignar.mutateAsync({ usuarioId, depositoId })
      toast.success('Asignacion actualizada')
    } catch {
      toast.error('Error al actualizar asignacion')
    }
  }

  if (isLoading) return <Skeleton />

  const usuariosDeposito = usuarios.filter(u => u.nivel >= 2)

  return (
    <div>
      <div style={{ fontSize: 13, color: 'var(--me-text-3)', marginBottom: 'var(--me-space-5)', maxWidth: 560 }}>
        Asigna un deposito a cada usuario para restringir su vista de stock.
        Los usuarios <strong>sin deposito asignado</strong> ven todo el stock (administradores).
        Los de nivel <strong>Deposito</strong> solo deben ver su sucursal.
      </div>

      {usuariosDeposito.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 24px', color: 'var(--me-text-3)' }}>
          <UsersIcon size={28} style={{ opacity: 0.3, marginBottom: 12 }} />
          <div>No hay usuarios para asignar</div>
        </div>
      ) : (
        <div className="me-card" style={{ overflow: 'hidden', padding: 0 }}>
          <table className="me-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Usuario</th>
                <th>Rol</th>
                <th>Deposito asignado</th>
              </tr>
            </thead>
            <tbody>
              {usuariosDeposito.map((u, i) => (
                <tr key={u.id} style={{ background: i % 2 !== 0 ? 'var(--me-surface-2)' : 'transparent', opacity: u.activo ? 1 : 0.45 }}>
                  <td>
                    <div style={{ fontWeight: 500 }}>{u.nombre}</div>
                    <div style={{ fontSize: 12, color: 'var(--me-text-3)' }}>{u.email}</div>
                  </td>
                  <td>
                    <span className={`me-pill ${u.nivel === 1 ? 'me-pill-acid' : u.nivel === 2 ? 'me-pill-ok' : u.nivel === 3 ? 'me-pill-warn' : 'me-pill-neutral'}`}
                      style={u.nivel === 1 ? { color: '#4a5800' } : undefined}>
                      {u.role_nombre}
                    </span>
                  </td>
                  <td style={{ minWidth: 200 }}>
                    <select
                      className="me-input"
                      style={{ padding: '6px 10px', fontSize: 13 }}
                      value={u.deposito_id ?? ''}
                      disabled={asignar.isPending}
                      onChange={e => handleAsignar(u.id, e.target.value || null)}
                    >
                      <option value="">— Sin asignar (ve todo) —</option>
                      {depositos.filter(d => d.activo).map(d => (
                        <option key={d.id} value={d.id}>{d.nombre}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ── Tab 3: Stock unificado ────────────────────────────────────────────────────

function TabStockUnificado() {
  const { data: items = [], isLoading } = useStockUnificado()
  const [busqueda, setBusqueda] = useState('')

  const filtrados = busqueda.trim()
    ? items.filter(it =>
        it.nombre.toLowerCase().includes(busqueda.toLowerCase()) ||
        (it.marca ?? '').toLowerCase().includes(busqueda.toLowerCase()) ||
        (it.sku ?? '').toLowerCase().includes(busqueda.toLowerCase())
      )
    : items

  if (isLoading) return <Skeleton />

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--me-space-4)', marginBottom: 'var(--me-space-5)' }}>
        <div style={{ fontSize: 13, color: 'var(--me-text-3)', flex: 1, maxWidth: 420 }}>
          Vista consolidada: stock total por producto sumando todos los depositos activos.
          Solo visible para administradores.
        </div>
        <input
          className="me-input"
          style={{ width: 220 }}
          placeholder="Buscar producto..."
          value={busqueda}
          onChange={e => setBusqueda(e.target.value)}
        />
      </div>

      {filtrados.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 24px', color: 'var(--me-text-3)' }}>
          <BoxIcon size={28} style={{ opacity: 0.3, marginBottom: 12 }} />
          <div>{busqueda ? 'Sin resultados para la busqueda' : 'No hay productos con stock registrado'}</div>
        </div>
      ) : (
        <div className="me-card" style={{ overflow: 'hidden', padding: 0 }}>
          <table className="me-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Producto</th>
                <th>Categoria</th>
                <th style={{ textAlign: 'right' }}>Min.</th>
                <th style={{ textAlign: 'right' }}>Total</th>
                <th>Por deposito</th>
              </tr>
            </thead>
            <tbody>
              {filtrados.map((it, i) => {
                const bajo = it.stock_total <= it.stock_minimo && it.stock_minimo > 0
                return (
                  <tr key={it.id} style={{ background: i % 2 !== 0 ? 'var(--me-surface-2)' : 'transparent' }}>
                    <td>
                      <div style={{ fontWeight: 500 }}>{it.nombre}</div>
                      {(it.marca || it.sku) && (
                        <div style={{ fontSize: 11, color: 'var(--me-text-4)', fontFamily: 'var(--me-mono)' }}>
                          {[it.marca, it.sku].filter(Boolean).join(' · ')}
                        </div>
                      )}
                    </td>
                    <td style={{ fontSize: 13, color: 'var(--me-text-3)' }}>
                      {it.categoria ?? '—'}
                    </td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--me-mono)', fontSize: 13, color: 'var(--me-text-3)' }}>
                      {it.stock_minimo}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <span
                        style={{
                          fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"',
                          fontWeight: 700, fontSize: 15,
                          color: bajo ? 'var(--me-bad)' : it.stock_total === 0 ? 'var(--me-text-4)' : 'var(--me-text)',
                        }}
                      >
                        {it.stock_total}
                      </span>
                      {bajo && (
                        <span className="me-pill me-pill-bad" style={{ marginLeft: 6, fontSize: 10 }}>bajo</span>
                      )}
                    </td>
                    <td>
                      {it.por_deposito && it.por_deposito.length > 0 ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {it.por_deposito.map(pd => (
                            <span
                              key={pd.deposito_id}
                              style={{
                                display: 'inline-flex', alignItems: 'center', gap: 4,
                                background: 'var(--me-surface-2)', borderRadius: 6,
                                padding: '2px 8px', fontSize: 12,
                                border: '1px solid var(--me-line-2)',
                              }}
                            >
                              <span style={{ color: 'var(--me-text-3)' }}>{pd.deposito}</span>
                              <span style={{ fontFamily: 'var(--me-mono)', fontWeight: 600, color: 'var(--me-text)' }}>
                                {pd.cantidad}
                              </span>
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span style={{ color: 'var(--me-text-4)', fontSize: 12 }}>Sin stock en depositos</span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          <div style={{ padding: '10px 16px', borderTop: '1px solid var(--me-line-2)', fontSize: 12, color: 'var(--me-text-4)' }}>
            {filtrados.length} producto{filtrados.length !== 1 ? 's' : ''}
            {busqueda && ` de ${items.length} totales`}
          </div>
        </div>
      )}
    </div>
  )
}

// ── Shared ────────────────────────────────────────────────────────────────────

function ActionBtn({ children, onClick, title }: {
  children: React.ReactNode; onClick: () => void; title: string
}) {
  return (
    <button onClick={onClick} title={title} style={{
      background: 'none', border: 'none', cursor: 'pointer',
      color: 'var(--me-text-3)', borderRadius: 6, padding: 5,
      display: 'flex', alignItems: 'center',
      transition: 'background var(--me-dur-short), color var(--me-dur-short)',
    }}
      onMouseOver={e => { e.currentTarget.style.background = 'var(--me-surface-2)'; e.currentTarget.style.color = 'var(--me-text)' }}
      onMouseOut={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = 'var(--me-text-3)' }}
    >
      {children}
    </button>
  )
}

function EmptyState({ onNuevo }: { onNuevo: () => void }) {
  return (
    <div style={{ textAlign: 'center', padding: '64px 24px', color: 'var(--me-text-3)' }}>
      <WarehouseIcon size={32} style={{ opacity: 0.3, marginBottom: 12 }} />
      <div style={{ fontWeight: 600, marginBottom: 6, color: 'var(--me-text-2)' }}>Sin depositos</div>
      <div style={{ fontSize: 13, marginBottom: 'var(--me-space-5)' }}>
        Crea un deposito para empezar a gestionar stock por ubicacion
      </div>
      <button className="me-btn me-btn-primary" onClick={onNuevo}>
        <PlusIcon size={15} /> Nuevo deposito
      </button>
    </div>
  )
}

function Skeleton() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {[...Array(4)].map((_, i) => (
        <div key={i} style={{ height: 48, background: 'var(--me-surface-3)', borderRadius: 8, opacity: 1 - i * 0.2 }} />
      ))}
    </div>
  )
}
